// =====================================================================
// Aba 2 — Profissionais (Fase 3, S38).
// CRUD da tabela clinic_professionals via endpoints v2 do n8n.
// Campos editaveis (whitelist do workflow):
//   name (req), social_name, specialty, license (CRM), bio,
//   schedule_json (custom: WorkingHoursEditor),
//   max_patients_per_day, avg_consultation_minutes,
//   consultation_types, accepts_insurance, insurance_list,
//   is_active, is_lead, sort_order, is_bookable,
//   default_price, default_price_text, crm_user_id
// =====================================================================
import React from "react";
import { Chip } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import StarIcon from "@material-ui/icons/Star";

import CrudTable from "../components/CrudTable";
import CrudDialog from "../components/CrudDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import WorkingHoursEditor from "../components/WorkingHoursEditor";
import useCrudResource from "../hooks/useCrudResource";

import {
  listProfessionals,
  createProfessional,
  updateProfessional,
  deleteProfessional
} from "../../../services/clinicAdminService";

const COLUMNS = [
  { key: "name", label: "Nome" },
  { key: "specialty", label: "Especialidade" },
  { key: "license", label: "CRM", width: 110 },
  {
    key: "is_active",
    label: "Ativo",
    width: 70,
    align: "center",
    render: (r) =>
      r.is_active ? (
        <CheckCircleIcon fontSize="small" htmlColor="#06c270" />
      ) : (
        "—"
      )
  },
  {
    key: "is_lead",
    label: "Principal",
    width: 90,
    align: "center",
    render: (r) =>
      r.is_lead ? <StarIcon fontSize="small" htmlColor="#ff9800" /> : "—"
  },
  {
    key: "sort_order",
    label: "Ordem",
    width: 80,
    align: "right",
    render: (r) => r.sort_order ?? "—"
  }
];

// fields config do dialog (declarativo)
const FIELDS = [
  { key: "name", label: "Nome", type: "text", required: true, colSpan: 6 },
  { key: "social_name", label: "Nome social", type: "text", colSpan: 6 },
  { key: "specialty", label: "Especialidade", type: "text", colSpan: 6 },
  { key: "license", label: "CRM", type: "text", colSpan: 6, placeholder: "CRM-PE 12345" },
  { key: "bio", label: "Bio", type: "textarea", rows: 3 },
  {
    key: "schedule_json",
    label: "Horários de atendimento",
    type: "custom",
    render: (value, setValue) => (
      <WorkingHoursEditor value={value} onChange={setValue} />
    ),
    helperText: "Por dia da semana. Vários intervalos por dia permitidos."
  },
  {
    key: "max_patients_per_day",
    label: "Máx. pacientes/dia",
    type: "number",
    colSpan: 4,
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "avg_consultation_minutes",
    label: "Duração média (min)",
    type: "number",
    colSpan: 4,
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "sort_order",
    label: "Ordem de exibição",
    type: "number",
    colSpan: 4,
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "consultation_types",
    label: "Tipos de consulta",
    type: "text",
    colSpan: 6,
    placeholder: "presencial, teleconsulta",
    helperText: "Lista livre, separe por vírgula"
  },
  {
    key: "insurance_list",
    label: "Convênios aceitos",
    type: "text",
    colSpan: 6,
    placeholder: "Unimed, Amil, Bradesco"
  },
  {
    key: "default_price",
    label: "Preço padrão (R$)",
    type: "number",
    colSpan: 4,
    step: 0.01,
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "default_price_text",
    label: "Texto do preço",
    type: "text",
    colSpan: 8,
    placeholder: "R$ 350,00 (particular) ou Convênio"
  },
  { key: "is_active", label: "Ativo", type: "switch", colSpan: 4 },
  { key: "is_lead", label: "Profissional principal", type: "switch", colSpan: 4 },
  { key: "is_bookable", label: "Aceita agendamento online", type: "switch", colSpan: 4 },
  { key: "accepts_insurance", label: "Aceita convênio", type: "switch", colSpan: 6 },
  {
    key: "crm_user_id",
    label: "ID do usuário no CRM (opcional)",
    type: "number",
    colSpan: 6,
    helperText: "Vincula ao login do CRM Genius",
    transform: (v) => (v === "" ? null : Number(v))
  }
];

const ProfissionaisTab = ({ slug }) => {
  const crud = useCrudResource({
    slug,
    list: listProfessionals,
    create: createProfessional,
    update: updateProfessional,
    remove: deleteProfessional,
    resourceLabel: "profissional"
  });

  return (
    <>
      <CrudTable
        columns={COLUMNS}
        rows={crud.rows}
        loading={crud.loading}
        onAdd={crud.handleAdd}
        onEdit={crud.handleEdit}
        onDelete={crud.handleDelete}
        searchPlaceholder="Buscar profissional…"
        searchFields={["name", "specialty", "license"]}
      />

      <CrudDialog
        open={crud.dialogOpen}
        onClose={crud.handleCloseDialog}
        onSave={crud.handleSave}
        title={crud.dialogInitial ? "Editar profissional" : "Novo profissional"}
        fields={FIELDS}
        initialValues={crud.dialogInitial}
        saving={crud.saving}
      />

      <ConfirmDeleteDialog
        open={!!crud.deleteTarget}
        onClose={crud.handleCancelDelete}
        onConfirm={crud.handleConfirmDelete}
        itemLabel={crud.deleteTarget?.name}
        saving={crud.saving}
      />
    </>
  );
};

export default ProfissionaisTab;
